import React from 'react';
import type { PlacementEtudiant, ResultatsParSalle } from '../../services/brassageApi';

// ─── Helper ────────────────────────────────────────────────────────────────

const fmtDate = (d: string | null): string => {
  if (!d) return '';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return d;
};

export interface BrassagePrintOptions {
  format?: 'A4' | 'A3';
  orientation?: 'portrait' | 'landscape';
  fontFamily?: string;
  fontScale?: number;
  /** Infos identité institut (ville + chef scolarité) pour le pied de page.
   *  Injecté depuis `useGetInfoInstitutQuery` — évite les valeurs codées en
   *  dur (« Fait à Pointe-Noire » / « Donald Christ MBEMBA LOKO ») qui
   *  reviennent apparaître sur BZ après chaque déploiement. */
  institutVille?: string;
  chefScolariteNom?: string;
  chefScolariteTitre?: string;
}

// ─── buildBrassagePageHTML — une page par salle/classe ──────────────────────

function buildBrassagePageHTML(
  title: string,
  subtitle: string,
  columns: { label: string; key: string; width?: string; align?: string }[],
  rows: PlacementEtudiant[],
  effectif: number,
  anneeAcademique: string,
  opts?: BrassagePrintOptions,
): string {
  const scale = (opts?.fontScale || 100) / 100;
  const headerRows = rows.map((p, i) => {
    const bgColor = i % 2 === 0 ? '#fff' : '#f5f5f5';
    const cells = columns.map(col => {
      let val = '';
      switch (col.key) {
        case 'num': val = String(i + 1); break;
        case 'nom': val = p.etudiant_nom; break;
        case 'prenoms': val = p.etudiant_prenoms; break;
        case 'sexe': val = p.etudiant_sexe; break;
        case 'date_naissance': val = fmtDate(p.etudiant_date_naissance); break;
        case 'lieu_naissance': val = p.etudiant_lieu_naissance; break;
        case 'classe': val = p.etudiant_classe; break;
        case 'site': val = p.site_nom; break;
        case 'salle': val = p.salle_nom; break;
      }
      return `<td style="border:1px solid #000;padding:3px 6px;font-size:${10 * scale}px;text-align:${col.align || 'left'};">${val}</td>`;
    }).join('');
    return `<tr style="background:${bgColor};">${cells}</tr>`;
  }).join('');

  const colHeaders = columns.map(col =>
    `<th style="border:1px solid #000;padding:4px 6px;font-size:${9 * scale}px;font-weight:bold;text-align:center;background:#FFD700;${col.width ? 'width:' + col.width + ';' : ''}">${col.label}</th>`
  ).join('');

  return `
<!-- En-tête institutionnel -->
<table style="width:100%;margin-bottom:6px;"><tr>
  <td style="width:80px;vertical-align:top;text-align:center;">
    <img src="/logo.png" alt="Logo" style="width:75px;height:75px;object-fit:contain;" onerror="this.style.display='none'" />
  </td>
  <td style="vertical-align:top;text-align:center;line-height:1.4;">
    <div style="font-weight:bold;font-size:${13 * scale}px;text-transform:uppercase;">INSTITUT INTERNATIONAL POLYTECHNIQUE & COMMERCE</div>
    <div style="font-size:${9 * scale}px;">Agrément définitif par l'Etat S/N° 196 MES-CAB.DG-DAAC du 21/04/2021</div>
    <div style="font-weight:bold;font-size:${11 * scale}px;margin-top:6px;text-transform:uppercase;">SERVICE DE LA SCOLARITE ET DES EXAMENS</div>
  </td>
</tr></table>

<!-- Titre -->
<div style="text-align:center;font-weight:bold;font-size:${16 * scale}px;margin:10px 0 4px 0;text-transform:uppercase;text-decoration:underline;">
  ${title}
</div>

<!-- Sous-titre (site/salle/classe) -->
<div style="text-align:center;font-size:${10 * scale}px;margin-bottom:8px;">
  <span style="margin-right:20px;">Année Académique : <strong>${anneeAcademique}</strong></span>
  ${subtitle}
  <span style="margin-left:20px;">Effectif : <strong>${effectif}</strong></span>
</div>

<!-- Tableau -->
<table style="width:100%;border-collapse:collapse;">
  <thead><tr>${colHeaders}</tr></thead>
  <tbody>${headerRows}</tbody>
</table>

<!-- Pied de page (ville + chef configurables via useGetInfoInstitutQuery) -->
<div style="margin-top:20px;text-align:right;font-size:${10 * scale}px;">
  <div>Fait à ${opts?.institutVille || 'Pointe-Noire'}, le ...................................</div>
  <div style="margin-top:10px;font-weight:bold;">${opts?.chefScolariteTitre || 'Le Chef de Service de la Scolarité et des Examens'}</div>
  <div style="margin-top:30px;">................................................................</div>
  <div style="margin-top:2px;font-weight:bold;">${opts?.chefScolariteNom || 'Donald Christ MBEMBA LOKO'}</div>
</div>
`;
}

// ─── buildBrassageListeHTML — génère le HTML complet multi-pages ─────────

export function buildBrassageListeHTML(
  groups: { title: string; subtitle: string; rows: PlacementEtudiant[]; effectif: number }[],
  mode: 'par_salle' | 'par_classe' | 'par_site',
  anneeAcademique: string,
  opts?: BrassagePrintOptions,
): string {
  // Colonnes selon le mode
  const columns = mode === 'par_classe'
    ? [
        { label: 'N°', key: 'num', width: '30px', align: 'center' },
        { label: 'Nom', key: 'nom', width: '22%' },
        { label: 'Prénom', key: 'prenoms', width: '22%' },
        { label: 'Sexe', key: 'sexe', width: '35px', align: 'center' },
        { label: 'Né(e)', key: 'date_naissance', width: '70px', align: 'center' },
        { label: 'Lieu de Nce', key: 'lieu_naissance' },
        { label: 'Site', key: 'site', align: 'center' },
        { label: 'Salle', key: 'salle', align: 'center' },
      ]
    : [
        { label: 'N°', key: 'num', width: '30px', align: 'center' },
        { label: 'Nom', key: 'nom', width: '25%' },
        { label: 'Prénom', key: 'prenoms', width: '22%' },
        { label: 'Sexe', key: 'sexe', width: '35px', align: 'center' },
        { label: 'Né(e)', key: 'date_naissance', width: '70px', align: 'center' },
        { label: 'Lieu de Nce', key: 'lieu_naissance' },
        { label: 'Classe', key: 'classe', align: 'center' },
      ];

  const mainTitle = mode === 'par_classe' ? 'LISTES BRASSEES PAR CLASSE' : 'LISTES BRASSEES PAR SALLE';

  const pages = groups.map((group, i) => {
    const pageHTML = buildBrassagePageHTML(
      mainTitle,
      group.subtitle,
      columns,
      group.rows,
      group.effectif,
      anneeAcademique,
      opts,
    );
    const pageBreak = i < groups.length - 1 ? 'page-break-after:always;' : '';
    return `<div style="${pageBreak}">${pageHTML}
      <div style="margin-top:8px;font-size:8px;display:flex;justify-content:space-between;color:#666;">
        <span>Institut International 2i</span>
        <span>${mainTitle.toLowerCase()}</span>
        <span>${i + 1}/${groups.length}</span>
      </div>
    </div>`;
  }).join('');

  const pageFormat = opts?.format || 'A4';
  const orientation = opts?.orientation || 'portrait';
  const ff = opts?.fontFamily || 'Arial, Helvetica, sans-serif';
  const scale = (opts?.fontScale || 100) / 100;
  const isLandscape = orientation === 'landscape';

  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>${mainTitle}</title>
<style>
  @page { size: ${pageFormat} ${orientation}; margin: ${isLandscape ? '8mm 10mm' : '10mm 12mm'}; }
  @media print {
    html, body { margin: 0 !important; padding: 0 !important; }
  }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
  body { font-family: ${ff}; font-size: ${11 * scale}px; color: #000; margin: 0; padding: 10px 15px; }
  table { border-collapse: collapse; }
</style>
</head>
<body>
${pages}
</body>
</html>`;
}

// ─── Helpers pour construire les groupes ─────────────────────────────────

export function buildGroupsParSalle(resultats: ResultatsParSalle[]): { title: string; subtitle: string; rows: PlacementEtudiant[]; effectif: number }[] {
  return resultats.map(r => ({
    title: 'LISTES BRASSEES PAR SALLE',
    subtitle: `Site : <strong>${r.site_nom}</strong> &nbsp;|&nbsp; Salle : <strong>${r.salle_nom}</strong>`,
    rows: r.placements,
    effectif: r.effectif,
  }));
}

export function buildGroupsParClasse(resultats: { classe: string; effectif: number; placements: PlacementEtudiant[] }[]): { title: string; subtitle: string; rows: PlacementEtudiant[]; effectif: number }[] {
  return resultats.map(r => ({
    title: 'LISTES BRASSEES PAR CLASSE',
    subtitle: `Classe : <strong>${r.classe}</strong>`,
    rows: r.placements,
    effectif: r.effectif,
  }));
}

// ─── Composant React pour aperçu iframe ─────────────────────────────────

interface BrassageListePreviewProps {
  html: string;
}

const BrassageListePreview: React.FC<BrassageListePreviewProps> = ({ html }) => {
  return (
    <div style={{ width: '100%', overflow: 'auto' }}>
      <iframe
        srcDoc={html}
        style={{ width: '100%', minHeight: '80vh', border: 'none' }}
        title="Aperçu Liste Brassée"
      />
    </div>
  );
};

export default BrassageListePreview;
